import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useUserJourneys } from "@/hooks/useUserJourneys";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, GitBranch } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface UserJourneyFlowProps {
  startDate: string;
  endDate: string;
}

export const UserJourneyFlow = ({ startDate, endDate }: UserJourneyFlowProps) => {
  const { data: journeys, isLoading } = useUserJourneys({ startDate, endDate, limit: 5 });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>User Journey Flow</CardTitle>
          <CardDescription>Häufigste Nutzerpfade</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px]" />
        </CardContent>
      </Card>
    );
  }

  if (!journeys || journeys.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            User Journey Flow
          </CardTitle>
          <CardDescription>Häufigste Nutzerpfade</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Keine Journey-Daten verfügbar. Events müssen erst gesammelt werden.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="h-5 w-5" />
          User Journey Flow
        </CardTitle>
        <CardDescription>Top {journeys.length} häufigste Nutzerpfade</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {journeys.map((journey, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono">
                  {journey.count}x
                </Badge>
                <span className="text-sm font-medium">
                  {journey.conversion_rate > 0 && (
                    <Badge variant="default" className="mr-2">
                      {journey.conversion_rate.toFixed(1)}% Conv.
                    </Badge>
                  )}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {journey.path.map((page, pageIndex) => (
                <div key={pageIndex} className="flex items-center gap-2">
                  <Badge 
                    variant="secondary" 
                    className="font-mono text-xs max-w-[150px] truncate"
                    title={page}
                  >
                    {page}
                  </Badge>
                  {pageIndex < journey.path.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
